// LongLivedTokensCard.tsx — lists / creates / revokes the signed-in user's
// long-lived account access tokens (authz /api/me/access-tokens). The plaintext
// token is shown exactly once, right after creation.
import { useCallback, useEffect, useMemo, useState } from 'react'
import Card from 'react-bootstrap/Card'
import Table from 'react-bootstrap/Table'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import Alert from 'react-bootstrap/Alert'
import Spinner from 'react-bootstrap/Spinner'
import Text from 'common/ui/Text'
import Dropdown from 'common/ui/Dropdown'
import { useConfirm } from 'common/ui/ConfirmDialog'
import { apiFetch } from '../hooks/useAuth'

interface AccessToken {
    id: string
    name: string
    prefix: string
    scopes: string[]
    created_at: string
    last_used_at?: string | null
    expires_at?: string | null
    revoked_at?: string | null
}

const EXPIRY_OPTIONS: { label: string; seconds: number }[] = [
    { label: 'Never', seconds: 0 },
    { label: '30 days', seconds: 30 * 24 * 3600 },
    { label: '90 days', seconds: 90 * 24 * 3600 },
    { label: '1 year', seconds: 365 * 24 * 3600 },
]

const fmtDate = (s?: string | null) => (s ? new Date(s).toLocaleDateString() : '—')

const LongLivedTokensCard = ({ availableScopes }: { availableScopes: string[] }) => {
    const [tokens, setTokens] = useState<AccessToken[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')

    // Create-modal state.
    const [showCreate, setShowCreate] = useState(false)
    const [name, setName] = useState('')
    const [prefix, setPrefix] = useState('az_')
    const [expirySeconds, setExpirySeconds] = useState(0)
    const [selectedScopes, setSelectedScopes] = useState<Set<string>>(new Set())
    const [creating, setCreating] = useState(false)
    const [createError, setCreateError] = useState('')

    // Plaintext shown once after creation.
    const [newPlaintext, setNewPlaintext] = useState('')

    const sortedScopes = useMemo(() => [...availableScopes].sort(), [availableScopes])

    const load = useCallback(async () => {
        setLoading(true)
        try {
            const res = await apiFetch('/api/me/access-tokens')
            if (!res.ok) throw new Error(`Failed to load tokens (${res.status})`)
            setTokens(await res.json())
            setError('')
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to load tokens')
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => { load() }, [load])

    const openCreate = () => {
        setName('')
        setPrefix('az_')
        setExpirySeconds(0)
        setSelectedScopes(new Set(sortedScopes)) // all granted scopes selected by default
        setCreateError('')
        setShowCreate(true)
    }

    const toggleScope = (scope: string) => {
        setSelectedScopes(prev => {
            const next = new Set(prev)
            if (next.has(scope)) next.delete(scope)
            else next.add(scope)
            return next
        })
    }

    const submitCreate = async () => {
        setCreating(true)
        setCreateError('')
        try {
            // Omit scopes when every granted scope is selected → pins to the full set.
            const allSelected = selectedScopes.size === sortedScopes.length
            const body: Record<string, unknown> = { name, prefix }
            if (expirySeconds > 0) body.expires_in = expirySeconds
            if (!allSelected) body.scopes = [...selectedScopes]

            const res = await apiFetch('/api/me/access-tokens', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            })
            if (!res.ok) {
                const msg = await res.json().catch(() => ({}))
                throw new Error(msg.error || `Create failed (${res.status})`)
            }
            const created = await res.json()
            setNewPlaintext(created.token)
            setShowCreate(false)
            await load()
        } catch (e) {
            setCreateError(e instanceof Error ? e.message : 'Create failed')
        } finally {
            setCreating(false)
        }
    }

    const [confirm, confirmDialog] = useConfirm()

    const revoke = async (id: string) => {
        const ok = await confirm({
            title: 'Revoke Token',
            body: 'Revoke this token? Anything using it stops working immediately.',
            confirmLabel: 'Revoke',
        })
        if (!ok) return
        try {
            const res = await apiFetch(`/api/me/access-tokens/${id}`, { method: 'DELETE' })
            if (!res.ok && res.status !== 404) throw new Error(`Revoke failed (${res.status})`)
            await load()
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Revoke failed')
        }
    }

    return (
        <Card className="mb-3">
            <Card.Header>Long-Lived Tokens</Card.Header>
            <Card.Body>
                {newPlaintext && (
                    <Alert variant="success" dismissible onClose={() => setNewPlaintext('')}>
                        <div className="fw-bold mb-1">Token created — copy it now, it won't be shown again.</div>
                        <div className="d-flex align-items-center gap-2">
                            <code className="flex-grow-1 text-break">{newPlaintext}</code>
                            <Button size="sm" variant="outline-secondary" onClick={() => navigator.clipboard?.writeText(newPlaintext)}>Copy</Button>
                        </div>
                    </Alert>
                )}

                {error && <Alert variant="danger">{error}</Alert>}

                {loading ? (
                    <div className="d-flex justify-content-center p-3"><Spinner animation="border" size="sm" /></div>
                ) : tokens.length === 0 ? (
                    <span className="text-muted small">No long-lived tokens. Create one for CLI access or automation.</span>
                ) : (
                    <Table size="sm" responsive className="m-0 align-middle">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Prefix</th>
                                <th>Scopes</th>
                                <th>Created</th>
                                <th>Last used</th>
                                <th>Expires</th>
                                <th>Status</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {tokens.map(t => (
                                <tr key={t.id}>
                                    <td className="fw-bold">{t.name}</td>
                                    <td><code>{t.prefix}</code></td>
                                    <td>{t.scopes?.length ?? 0}</td>
                                    <td>{fmtDate(t.created_at)}</td>
                                    <td>{fmtDate(t.last_used_at)}</td>
                                    <td>{t.expires_at ? fmtDate(t.expires_at) : 'Never'}</td>
                                    <td>
                                        {t.revoked_at
                                            ? <span className="badge text-bg-secondary">Revoked</span>
                                            : <span className="badge text-bg-success">Active</span>}
                                    </td>
                                    <td className="text-end">
                                        {!t.revoked_at && (
                                            <Button size="sm" variant="outline-danger" onClick={() => revoke(t.id)}>Revoke</Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Card.Body>
            <Card.Footer className="d-flex justify-content-end">
                <Button variant="success" onClick={openCreate}>Create</Button>
            </Card.Footer>

            <Modal show={showCreate} onHide={() => setShowCreate(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Create Long-Lived Token</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {createError && <Alert variant="danger">{createError}</Alert>}
                    <Form.Group className="mb-3 d-flex flex-column">
                        <Form.Label>Name</Form.Label>
                        <Text value={name} onChange={e => setName(e.target.value)} placeholder="e.g. ci-runner" minWidth={24} autoFocus />
                    </Form.Group>
                    <Form.Group className="mb-3 d-flex flex-column">
                        <Form.Label>Prefix</Form.Label>
                        <Text value={prefix} onChange={e => setPrefix(e.target.value)} placeholder="az_" minWidth={10} />
                        <Form.Text muted>Lowercase, ends with “_” (e.g. <code>aq_</code>). Identifies the token's origin.</Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3 d-flex flex-column">
                        <Form.Label>Expires</Form.Label>
                        <Dropdown
                            value={String(expirySeconds)}
                            onChange={e => setExpirySeconds(Number(e.target.value))}
                            minWidth={10}
                            options={EXPIRY_OPTIONS.map(o => ({ value: String(o.seconds), label: o.label }))}
                        />
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Scopes <span className="text-muted small">(pinned at creation)</span></Form.Label>
                        <div style={{ maxHeight: 180, overflowY: 'auto' }} className="card p-2">
                            {sortedScopes.length === 0
                                ? <span className="text-muted small">You have no scopes to grant.</span>
                                : sortedScopes.map(s => (
                                    <Form.Check
                                        key={s}
                                        type="checkbox"
                                        id={`scope-${s}`}
                                        label={<code>{s}</code>}
                                        checked={selectedScopes.has(s)}
                                        onChange={() => toggleScope(s)}
                                    />
                                ))}
                        </div>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
                    <Button variant="primary" onClick={submitCreate} disabled={creating || !name || !prefix}>
                        {creating ? 'Creating…' : 'Create'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {confirmDialog}
        </Card>
    )
}

export default LongLivedTokensCard
